import React, { useEffect, useState } from 'react';
import { authorizedPost, listAllMealPlans, resetAllMealPlansData, toggleLoading } from '../../store/actions';
import { Box, Button, CircularProgress, MenuItem, Modal, Stack, Typography } from '@mui/material';
import { tableStyle } from '../../components';
import { DataGrid } from '@mui/x-data-grid';
import {
    ACTIVATE_PMS_RATEPLAN,
    ADD_NEW_ROOMTYPE_PMS_RATEPLAN,
    GET_AVAILABLE_PMS,
    LIST_ALL_PMS_RATEPLANS,
    UPDATE_ROOMTYPE_PMS_RATEPLAN,
} from '../../graphql';
import AvailabilityUpdation from '../../components/AvailabilityUpdation';
import { Add, Edit } from '@mui/icons-material';
import Header from '../../components/Header';
import { CssTextField } from '../../components/TextField';
import { useSelector } from 'react-redux';
import { toast } from "react-toastify";

function RoomtypePmsRatePlans({ roomtypeId, dispatch, colors }) {
    const [pmsRatePlansList, setPmsRatePlansList] = useState([]);
    const [availablePmss, setAvailablePmss] = useState([]);
    const [pmsListLoading, setPmsListLoading] = useState(false);

    const [editId, setEditId] = useState("");
    const [selectedPmsId, setSelectedPmsId] = useState("");
    const [selectedMealPlan, setSelectedMealPlan] = useState("");
    const [pmsRateCode, setPmsRateCode] = useState("");
    const [name, setName] = useState("");
    const [open, setOpen] = useState(false);
    // "add" | "edit"
    const [mode, setMode] = useState("add");

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { sm: 400, xs: "90%" },
        bgcolor: colors.primary[400],
        outline: "none",
        boxShadow: 24,
        p: 2,
    };

    const { isLoading, mealPlans } = useSelector(
        ({
            loading,
            booking: { mealPlans: { list } }
        }) => ({
            isLoading: loading,
            mealPlans: list,
        })
    );

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [])

    const fetchData = async () => {
        dispatch(toggleLoading(true));
        let response = await authorizedPost(LIST_ALL_PMS_RATEPLANS, { roomtypeId: roomtypeId });
        if (response?.data?.list_all_pms_rateplans?.data) {
            setPmsRatePlansList(response.data.list_all_pms_rateplans.data);
        }

        dispatch(resetAllMealPlansData());
        dispatch(listAllMealPlans({ roomtypeId: roomtypeId }));
        dispatch(toggleLoading(false));
    }

    const fetchAvailablePmss = async () => {
        setPmsListLoading(true);
        const response = await authorizedPost(GET_AVAILABLE_PMS);
        if (response?.status && response?.data?.get_available_pms?.data) {
            setAvailablePmss(response.data.get_available_pms.data);
        }
        setPmsListLoading(false);
    }

    const resetForm = () => {
        setEditId("");
        setSelectedPmsId("");
        setSelectedMealPlan("");
        setPmsRateCode("");
        setName("");
    }

    const handleOpenAddModal = () => {
        setMode("add");
        resetForm();
        setOpen(true);
        fetchAvailablePmss();
    }

    const handleOpenEditModal = (row) => {
        setMode("edit");
        setEditId(row?._id ?? "");
        setSelectedPmsId(row?.pms_id ?? "");
        setSelectedMealPlan(row?.meal_plan_id?._id ?? "");
        setPmsRateCode(row?.pms_rate_code ?? "");
        setName(row?.name ?? "");
        setOpen(true);
        fetchAvailablePmss();
    }

    const handleClose = () => {
        setOpen(false);
        resetForm();
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedPmsId) {
            toast.error("Please select a PMS");
            return;
        }
        if (!pmsRateCode?.trim()) {
            toast.error("PMS rate code is required");
            return;
        }
        if (!selectedMealPlan) {
            toast.error("Please select a meal plan");
            return;
        }

        dispatch(toggleLoading(true));

        const payload = {
            id: mode === "edit" ? editId : roomtypeId,
            pmsId: Number(selectedPmsId),
            pmsRateCode: pmsRateCode.trim(),
            mealPlanId: selectedMealPlan,
            name: name,
        };
        if (mode === "edit") {
            payload.roomtypeId = roomtypeId;
        }

        const mutation = mode === "edit" ? UPDATE_ROOMTYPE_PMS_RATEPLAN : ADD_NEW_ROOMTYPE_PMS_RATEPLAN;
        const response = await authorizedPost(mutation, payload);

        if (response?.status) {
            const responseKey = mode === "edit" ? "update_roomtype_pms_rateplan" : "add_new_roomtype_pms_rateplan";
            const fallback = mode === "edit" ? "PMS rate plan updated" : "PMS rate plan added";
            toast.success(response?.data?.[responseKey]?.message || fallback);
            resetForm();
            setOpen(false);
            await fetchData();
        }
        dispatch(toggleLoading(false));
    }

    const handleToggleActivation = async (id) => {
        const response = await authorizedPost(ACTIVATE_PMS_RATEPLAN, { id });
        if (response?.status) {
            toast.success(response?.data?.activate_pms_rateplan?.message || "PMS rate plan updated");
            fetchData();
        }
    }

    const columns = [
        {
            field: "pms_rate_code",
            headerName: "Rate code",
            cellClassName: "name-column--cell",
            width: 200,
            renderCell: ({ row: { pms_rate_code } }) => pms_rate_code || "-",
        },
        {
            field: "name",
            headerName: "Name",
            width: 180,
            renderCell: ({ row: { name } }) => name || "-",
        },
        {
            field: "meal_plan_id",
            headerName: "Meal plan",
            width: 150,
            renderCell: ({ row: { meal_plan_id } }) => meal_plan_id?.name ?? "-",
        },
        {
            field: "is_active",
            headerName: "Availability",
            width: 130,
            renderCell: ({ row: { is_active, _id } }) => (
                <AvailabilityUpdation
                    isActive={is_active}
                    action={() => handleToggleActivation(_id)}
                />
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 120,
            sortable: false,
            renderCell: ({ row }) => (
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<Edit fontSize="small" />}
                    onClick={() => handleOpenEditModal(row)}
                >
                    Update
                </Button>
            ),
        },
    ];

    return (
        <>
            <Box mb={15}>
                <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    alignItems={"end"}
                    m="20px 0"
                >
                    <Typography variant='h4' fontWeight="bold">
                        PMS RATE PLANS
                    </Typography>
                    <Box>
                        <Button
                            sx={{
                                backgroundColor: colors.blueAccent[700],
                                color: colors.grey[100],
                                fontSize: "14px",
                                fontWeight: "bold",
                                padding: { sm: "10px 20px", xs: "5px 10px" },
                            }}
                            onClick={handleOpenAddModal}
                        >
                            <Add sx={{ mr: "10px" }} />
                            Add new rate plan
                        </Button>
                    </Box>
                </Box>
                <Box
                    m="8px 0 0 0"
                    height="350px"
                    sx={tableStyle(colors)}
                >
                    <DataGrid
                        rows={pmsRatePlansList}
                        columns={columns}
                        getRowId={(row) => row?._id}
                    />
                </Box>

                <Modal
                    open={open}
                    onClose={handleClose}
                >
                    <Box sx={modalStyle}>
                        <Header
                            title={mode === "edit" ? "EDIT RATE PLAN" : "ADD NEW RATE PLAN"}
                            titleVariant="h4"
                        />
                        <form onSubmit={handleSubmit}>
                            <Stack spacing={2}>
                                <CssTextField
                                    fullWidth
                                    required
                                    select
                                    size="small"
                                    label="PMS"
                                    value={selectedPmsId ?? ""}
                                    onChange={(e) => setSelectedPmsId(e.target.value)}
                                    disabled={pmsListLoading}
                                >
                                    {pmsListLoading ? (
                                        <MenuItem value="" disabled>Loading...</MenuItem>
                                    ) : availablePmss?.length ? (
                                        availablePmss.map((pms) => (
                                            <MenuItem value={pms.id} key={pms.id}>
                                                {pms.name}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem value="" disabled>No PMSs available</MenuItem>
                                    )}
                                </CssTextField>
                                <CssTextField
                                    fullWidth
                                    required
                                    size="small"
                                    type="text"
                                    label="PMS rate code"
                                    placeholder="PMS rate code"
                                    value={pmsRateCode ?? ""}
                                    onChange={(e) => setPmsRateCode(e.target.value?.replace(" ", ""))}
                                />
                                <CssTextField
                                    fullWidth
                                    size="small"
                                    type="text"
                                    label="Rate plan name"
                                    placeholder="Rate plan name"
                                    value={name ?? ""}
                                    onChange={(e) => setName(e.target.value)}
                                />
                                <CssTextField
                                    fullWidth
                                    required
                                    select
                                    size="small"
                                    label="Meal plan"
                                    value={selectedMealPlan ?? ""}
                                    onChange={(e) => setSelectedMealPlan(e.target.value)}
                                >
                                    {mealPlans?.map((e, i) => (
                                        <MenuItem value={e._id} key={i} disabled={!e.is_active}>
                                            {e.name}
                                        </MenuItem>
                                    ))}
                                </CssTextField>
                                <Button
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    color="secondary"
                                    variant="contained"
                                    sx={{ fontSize: "15px" }}
                                    disabled={isLoading}
                                >
                                    {isLoading ? <CircularProgress color="inherit" size={24} /> : <b>{mode === "edit" ? "Update" : "Submit"}</b>}
                                </Button>
                            </Stack>
                        </form>
                    </Box>
                </Modal>
            </Box>
        </>
    )
}

export default RoomtypePmsRatePlans;
